/**
 * One import: what the machine read, and the surface a person corrects it on.
 *
 * THE SCREEN THE PHASE BRIEF ASKS FOR -- "show the operator what was read and let them correct
 * it" -- and the one that makes the promotion gate reachable by the person it depends on.
 *
 * THE CONFIDENCE IS SHOWN AND IS NEVER A GATE, and the page says so where somebody would
 * otherwise draw the wrong conclusion from it. Measured: Apple Vision over a generated ISO 13485
 * certificate returned `Certiticate Number: BW-13485-<020-114` for `Certificate Number:
 * BW-13485-2026-114` AT A REPORTED CONFIDENCE OF 1.00, and read `Standara:` for `Standard:` just
 * as confidently. Every line it got wrong was as certain as every line it got right. So the page
 * does not sort by confidence, does not hide high-confidence lines, and does not offer an
 * "accept everything above N" action -- there is no honest version of that control.
 *
 * CORRECTING AND CONFIRMING ARE DIFFERENT ACTS AND THE PAGE KEEPS THEM APART. Fixing a line says
 * that line was wrong. Confirming says a person has been through the document and it says what
 * the screen says -- which is what promotion requires, and which no number of corrections
 * implies. A document can be read perfectly and need no corrections at all; one with nine
 * corrections and no confirmation has had nine lines fixed by somebody who may have stopped at
 * line ten.
 *
 * PROMOTION IS NOT A TAB, IT IS THE END OF THE PAGE, and it is disabled with a sentence rather
 * than hidden. A disabled button that explains itself teaches the rule; a missing one leaves
 * somebody looking for it.
 *
 * READ-ONLY WHERE IT IS READ-ONLY, SAID PLAINLY. Staging a file needs the upload flow and is not
 * here yet.
 *
 * AND THE SCAN ITSELF IS NOT ON THIS PAGE YET, WHICH IS THE HONEST GAP. The service stores and
 * serves a normalised bounding box for every line precisely so a screen can put the text beside
 * the ink it was read from -- and this screen shows the transcript only. Proofreading a transcript
 * against nothing is weaker than proofreading it against the page, so the geometry is captured and
 * travels in the payload ready for that view; rendering the scan needs the attachment download URL
 * and a PDF rasteriser in the browser, and that is its own piece of work rather than something to
 * bolt on here. It is named rather than left for somebody to notice.
 */
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router";
import {Box, Button, Chip, CircularProgress, TextField, Tooltip, Typography} from "@mui/material";

import {AppContext} from "../util/AppContext";
import {makeCresAxios} from "../util/Axios";
import {Grey2, Grey7, Grey8, Grey17} from "../theme/styles";

type Line = {
    USID: string; PageNumber: number; LineNumber: number;
    Text?: string | null; Confidence?: number | null;
    BoxLeft?: number | null; BoxTop?: number | null;
    BoxWidth?: number | null; BoxHeight?: number | null;
    CorrectedText?: string | null; EffectiveText?: string | null; Corrected?: boolean;
};

type Event_ = {
    USID: string; EventType?: string | null; Detail?: string | null;
    EventDatetime?: string | null; PagesRead?: number | null;
    ContentSHA256?: string | null; DuplicateOfUSID?: string | null;
};

type Import_ = {
    USID: string; AttachmentUSID?: string | null;
    Origin?: string | null; OriginDetail?: string | null;
    ImportedDatetime?: string | null; ContentSHA256?: string | null; HashedBytes?: number | null;
    Status?: string | null;
    ReadByModel?: string | null; ReadDatetime?: string | null; OCRJobID?: string | null;
    OCRConfidence?: number | null; OCRNote?: string | null;
    ConfirmedByUSID?: string | null; ConfirmedDatetime?: string | null;
    DocumentTitle?: string | null; DocumentNumber?: string | null;
    DocumentRevision?: string | null; EffectiveDate?: string | null;
    DocumentTypeName?: string | null;
    PromotedRevisionUSID?: string | null; ChangeOrderUSID?: string | null;
    ReadByAModel?: boolean; Confirmed?: boolean; MayPromote?: boolean;
    ListOfDocumentImportPage?: {DocumentImportPage?: Line[] | Line};
    ListOfDocumentImportEvent?: {DocumentImportEvent?: Event_[] | Event_};
};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

function when(value?: string | null): string {
    if (!value) return "—";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 16);
    return at.toLocaleString(undefined, {day: "numeric", month: "short", year: "numeric",
                                         hour: "2-digit", minute: "2-digit"});
}

function spell(value?: string | null): string {
    const text = String(value ?? "").replace(/_/g, " ").toLowerCase();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
}

export default function DocumentImportDetail() {
    const {usid} = useParams();
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);
    const navigate = useNavigate();

    const [row, setRow] = useState<Import_ | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [refusal, setRefusal] = useState<string | null>(null);

    //: Edits a person has typed but not saved, keyed "<page>:<line>".
    const [edits, setEdits] = useState<Record<string, string>>({});
    const [fields, setFields] = useState<Record<string, string>>({});

    const load = useCallback(() => {
        if (!usid) return;
        setLoading(true);
        api.get("t1/v1/document_imports", {params: {USID: usid}})
            .then((answer: any) => {
                const held: Import_ = answer?.data?.PlatformMessage?.DocumentImport;
                setRow(held ?? null);
                setFields({
                    DocumentTitle: held?.DocumentTitle ?? "",
                    DocumentNumber: held?.DocumentNumber ?? "",
                    DocumentRevision: held?.DocumentRevision ?? "",
                    DocumentTypeName: held?.DocumentTypeName ?? "",
                    EffectiveDate: held?.EffectiveDate ?? "",
                });
                setEdits({});
                setError(null);
            })
            .catch((problem: any) => {
                setRow(null);
                setError(problem?.response?.data?.Cause ?? "This import could not be read.");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usid]);

    useEffect(() => { load(); }, [load]);

    const lines = useMemo(
        () => listOf<Line>(row?.ListOfDocumentImportPage?.DocumentImportPage), [row]);
    const events = useMemo(
        () => listOf<Event_>(row?.ListOfDocumentImportEvent?.DocumentImportEvent), [row]);
    const pages = useMemo(
        () => Array.from(new Set(lines.map(one => one.PageNumber))).sort((a, b) => a - b), [lines]);

    /** Everything that changes the import goes through here, so one refusal surface serves all. */
    const act = useCallback((label: string, call: () => Promise<any>) => {
        setBusy(label);
        setRefusal(null);
        call()
            .then(() => load())
            .catch((problem: any) => {
                // THE SERVICE'S OWN SENTENCE, because these refusals are the product. "This import
                // is byte-for-byte a file already imported as ..." and "nobody has confirmed that
                // this reading matches the document" each tell somebody what to do next; a generic
                // "that did not work" tells them nothing and reads like an outage.
                setRefusal(problem?.response?.data?.Cause
                           ?? `${label} did not work, and the reason did not come back.`);
            })
            .finally(() => setBusy(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    const saveCorrections = useCallback((also: {Confirm?: boolean} = {}) => {
        const changed = Object.entries(edits).map(([key, text]) => {
            const [page, line] = key.split(":").map(Number);
            return {PageNumber: page, LineNumber: line, Text: text === "" ? null : text};
        });
        const body: any = {USID: usid, ...fields, ...also};
        if (changed.length) body.Lines = changed;
        return api.patch("t1/v1/document_imports", body);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [edits, fields, usid]);

    if (loading && !row && !error) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "24px"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Opening the import…</Typography>
        </Box>;
    }

    if (error || !row) {
        return <Box sx={{padding: "24px"}}>
            <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6, maxWidth: 760}}>
                {error ?? "This import could not be read."}
            </Typography>
        </Box>;
    }

    const dirty = Object.keys(edits).length > 0;
    const promoted = row.Status === "PROMOTED";
    const discarded = row.Status === "DISCARDED";
    const shut = promoted || discarded;

    return <Box sx={{padding: "24px", maxWidth: 1400}}>
        <Typography sx={{fontSize: 12.5, color: Grey7, cursor: "pointer", paddingBottom: "6px"}}
                    onClick={() => navigate("/document-imports")}>
            ← Document imports
        </Typography>

        <Box sx={{display: "flex", alignItems: "baseline", gap: "12px", flexWrap: "wrap"}}>
            <Typography sx={{fontSize: 20, color: Grey17}}>
                {row.DocumentTitle || "Untitled import"}
            </Typography>
            <Chip label={spell(row.Status)} size="small" variant="outlined"
                  sx={{fontSize: 11, height: 22}}/>
        </Box>

        {/* ── WHERE IT CAME FROM AND WHAT READ IT ─────────────────────────────────────────── */}
        <Box sx={{display: "flex", gap: "32px", flexWrap: "wrap", padding: "16px 0"}}>
            <Box>
                <Typography sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase"}}>
                    Origin
                </Typography>
                <Typography sx={{fontSize: 13, color: Grey17}}>{row.Origin}</Typography>
                {row.OriginDetail && (
                    <Typography sx={{fontSize: 12, color: Grey7}}>{row.OriginDetail}</Typography>
                )}
            </Box>
            <Box>
                <Typography sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase"}}>
                    Imported
                </Typography>
                <Typography sx={{fontSize: 13, color: Grey17}}>
                    {when(row.ImportedDatetime)}
                </Typography>
            </Box>
            <Box>
                <Typography sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase"}}>
                    Read by
                </Typography>
                <Typography sx={{fontSize: 13, color: Grey17}}>
                    {row.ReadByModel || "nothing has read it"}
                </Typography>
                {row.OCRConfidence !== null && row.OCRConfidence !== undefined && (
                    <Tooltip title="The LOWEST line's confidence, not the average -- an average
                                    hides the one line that went wrong among forty that did not.
                                    It is evidence and never a gate: a measured misreading of a
                                    certificate number came back at 1.00.">
                        <Typography sx={{fontSize: 12, color: Grey7}}>
                            lowest line {Math.round((row.OCRConfidence ?? 0) * 100)}%
                        </Typography>
                    </Tooltip>
                )}
            </Box>
            <Box>
                <Typography sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase"}}>
                    Fingerprint
                </Typography>
                <Tooltip title="A SHA-256 of the file's bytes -- not its S3 ETag, which for a
                                multipart upload is a hash of part hashes and differs for the same
                                bytes uploaded with a different part size.">
                    <Typography sx={{fontSize: 12, color: Grey8, fontFamily: "monospace"}}>
                        {String(row.ContentSHA256 ?? "").slice(0, 16)}…
                    </Typography>
                </Tooltip>
                <Typography sx={{fontSize: 11.5, color: Grey7}}>
                    over {(row.HashedBytes ?? 0).toLocaleString()} bytes
                </Typography>
            </Box>
        </Box>

        {/* ── THE WARNING THAT CARRIES THE WHOLE PHASE ────────────────────────────────────── */}
        {row.ReadByAModel && !row.Confirmed && !shut && (
            <Box sx={{backgroundColor: "#FFF8E1", border: "1px solid #E6C77A",
                      borderRadius: "4px", padding: "12px 14px", maxWidth: 900,
                      marginBottom: "16px"}}>
                <Typography sx={{fontSize: 13, color: "#6B4E00", lineHeight: 1.6}}>
                    A machine read this and nobody has checked it. An OCR engine reports a misread
                    line as confidently as a correct one — a certificate number has been seen come
                    back wrong at a reported confidence of 100% — so this cannot become a
                    controlled document until you have been through the pages and confirmed what
                    they say.
                </Typography>
            </Box>
        )}

        {row.OCRNote && (
            <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6, maxWidth: 900,
                             paddingBottom: "14px"}}>
                {row.OCRNote}
            </Typography>
        )}

        {refusal && (
            <Box sx={{backgroundColor: "#FDECEA", border: "1px solid #E6A69F",
                      borderRadius: "4px", padding: "12px 14px", maxWidth: 900,
                      marginBottom: "16px"}}>
                <Typography sx={{fontSize: 13, color: "#8C1D18", lineHeight: 1.6}}>
                    {refusal}
                </Typography>
            </Box>
        )}

        {/* ── WHAT IT SAYS IT IS ──────────────────────────────────────────────────────────── */}
        <Typography sx={{fontSize: 15, color: Grey17, paddingBottom: "4px"}}>
            What it says it is
        </Typography>
        <Typography sx={{fontSize: 12.5, color: Grey7, lineHeight: 1.6, maxWidth: 900,
                         paddingBottom: "12px"}}>
            Read these off the page. The title and the document type are what a promotion needs —
            the type decides the approval matrix and the numbering.
        </Typography>
        <Box sx={{display: "flex", gap: "14px", flexWrap: "wrap", paddingBottom: "22px"}}>
            {[["DocumentTitle", "Title"], ["DocumentNumber", "Number"],
              ["DocumentRevision", "Revision"], ["DocumentTypeName", "Document type"],
              ["EffectiveDate", "Effective date"]].map(([key, label]) => (
                <TextField key={key} label={label} size="small" disabled={shut}
                           value={fields[key] ?? ""}
                           onChange={event => setFields(
                               previous => ({...previous, [key]: event.target.value}))}
                           slotProps={{inputLabel: {shrink: true}}}
                           sx={{minWidth: key === "DocumentTitle" ? 300 : 170}}/>
            ))}
        </Box>

        {/* ── WHAT WAS READ ───────────────────────────────────────────────────────────────── */}
        <Typography sx={{fontSize: 15, color: Grey17, paddingBottom: "4px"}}>
            What was read
        </Typography>
        {lines.length === 0 && (
            <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 900,
                             paddingBottom: "14px"}}>
                {row.Status === "READING"
                    ? "A reading is in flight. Collect it to see what the engine made of the page."
                    : "Nothing has read this yet."}
            </Typography>
        )}
        {lines.length > 0 && (
            <Typography sx={{fontSize: 12.5, color: Grey7, lineHeight: 1.6, maxWidth: 900,
                             paddingBottom: "12px"}}>
                Each line as the engine blocked it, in reading order, with its own confidence.
                The lines are NOT sorted by confidence and the high ones are not hidden: the
                engine does not know which lines it got wrong, so there is nothing to sort by.
            </Typography>
        )}

        {pages.map(page => (
            <Box key={page} sx={{paddingBottom: "18px"}}>
                <Typography sx={{fontSize: 12, color: Grey7, textTransform: "uppercase",
                                 letterSpacing: "0.04em", paddingBottom: "6px"}}>
                    Page {page}
                </Typography>
                {lines.filter(one => one.PageNumber === page).map(one => {
                    const key = `${one.PageNumber}:${one.LineNumber}`;
                    const typed = edits[key];
                    const shown = typed !== undefined
                        ? typed
                        : (one.CorrectedText ?? one.Text ?? "");
                    return <Box key={one.USID}
                                sx={{display: "grid",
                                     gridTemplateColumns: "44px 1fr 92px",
                                     gap: "10px", alignItems: "center",
                                     padding: "4px 0",
                                     borderBottom: `1px solid ${Grey2}`}}>
                        <Typography sx={{fontSize: 11.5, color: Grey7}}>
                            {one.LineNumber}
                        </Typography>
                        <TextField size="small" fullWidth disabled={shut} value={shown}
                                   onChange={event => setEdits(
                                       previous => ({...previous, [key]: event.target.value}))}
                                   sx={{"& .MuiInputBase-input": {
                                            fontSize: 13,
                                            fontFamily: "monospace",
                                            color: one.Corrected || typed !== undefined
                                                ? "#1B5E20" : Grey17}}}/>
                        <Box>
                            <Typography sx={{fontSize: 11.5, color: Grey7, textAlign: "right"}}>
                                {one.Confidence === null || one.Confidence === undefined
                                    ? "—"
                                    : `${Math.round(one.Confidence * 100)}%`}
                            </Typography>
                            {(one.Corrected || typed !== undefined) && (
                                <Typography sx={{fontSize: 11, color: "#1B5E20",
                                                 textAlign: "right"}}>
                                    {typed !== undefined && !one.Corrected
                                        ? "edited" : "corrected"}
                                </Typography>
                            )}
                        </Box>
                    </Box>;
                })}
            </Box>
        ))}

        {/* ── THE ACTS ────────────────────────────────────────────────────────────────────── */}
        <Box sx={{display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center",
                  padding: "18px 0", borderTop: `1px solid ${Grey2}`}}>
            {!shut && row.Status !== "READING" && (
                <Tooltip title="Sends the file to AWS Textract. THIS COSTS MONEY, per page, and
                                needs the documentImport:ocr privilege. A second reading of the
                                same scan is a second charge for one answer.">
                    <span>
                        <Button variant="outlined" size="small" disabled={!!busy}
                                onClick={() => act("Reading it",
                                    () => api.post("t1/v1/document_import_readings",
                                                   {USID: usid}))}>
                            {row.ReadByAModel ? "Read it again" : "Read it with OCR"}
                        </Button>
                    </span>
                </Tooltip>
            )}

            {row.Status === "READING" && (
                <Button variant="outlined" size="small" disabled={!!busy}
                        onClick={() => act("Collecting the reading",
                            () => api.patch("t1/v1/document_import_readings", {USID: usid}))}>
                    Collect the reading
                </Button>
            )}

            {!shut && (
                <Button variant="outlined" size="small" disabled={!!busy || !dirty}
                        onClick={() => act("Saving the corrections",
                            () => saveCorrections())}>
                    Save corrections{dirty ? ` (${Object.keys(edits).length})` : ""}
                </Button>
            )}

            {/* CONFIRMING IS ITS OWN BUTTON. Folding it into "save" would make every correction a
                confirmation, and a person who fixed one line would have signed for the page. */}
            {!shut && row.ReadByAModel && !row.Confirmed && (
                <Tooltip title="Records that you have been through this reading and it matches the
                                document. This is what a promotion requires, and no number of
                                corrections implies it.">
                    <span>
                        <Button variant="contained" size="small" disabled={!!busy}
                                onClick={() => act("Confirming the reading",
                                    () => saveCorrections({Confirm: true}))}>
                            I have checked this against the document
                        </Button>
                    </span>
                </Tooltip>
            )}

            {!shut && (
                <Tooltip title={row.MayPromote && row.Confirmed
                    ? "Creates a controlled Document and opens a change order against it."
                    : !row.Confirmed
                        ? "Nobody has confirmed that this reading matches the document. An OCR "
                          + "engine reports a misread line as confidently as a correct one, so a "
                          + "reading no person has checked cannot become a controlled document."
                        : "This import has not been read yet."}>
                    <span>
                        <Button variant="contained" size="small"
                                disabled={!!busy || !row.Confirmed || !row.MayPromote
                                          || !fields.DocumentTitle || !fields.DocumentTypeName}
                                onClick={() => act("Promoting it",
                                    () => api.post("t1/v1/document_import_promotions",
                                                   {USID: usid,
                                                    DocumentTypeName: fields.DocumentTypeName}))}>
                            Make it a controlled document
                        </Button>
                    </span>
                </Tooltip>
            )}

            {!promoted && !discarded && (
                <Button size="small" color="inherit" disabled={!!busy}
                        onClick={() => act("Discarding it",
                            () => api.delete("t1/v1/document_imports",
                                             {data: {USID: usid}}))}>
                    Discard
                </Button>
            )}

            {busy && <Typography sx={{fontSize: 12.5, color: Grey7}}>{busy}…</Typography>}
        </Box>

        {promoted && (
            <Typography sx={{fontSize: 13, color: "#1B5E20", lineHeight: 1.6, maxWidth: 900}}>
                This is a controlled document now
                {row.ChangeOrderUSID
                    ? ", with a change order against it."
                    : " — but NO change order was attached, which means nothing approved it. " +
                      "Raise one against the document."}
            </Typography>
        )}

        {/* ── THE TRAIL ───────────────────────────────────────────────────────────────────── */}
        {events.length > 0 && (
            <Box sx={{paddingTop: "22px"}}>
                <Typography sx={{fontSize: 15, color: Grey17, paddingBottom: "8px"}}>
                    What has happened to it
                </Typography>
                {events.map(one => (
                    <Box key={one.USID}
                         sx={{display: "grid", gridTemplateColumns: "180px 160px 1fr",
                              gap: "12px", padding: "6px 0",
                              borderBottom: `1px solid ${Grey2}`}}>
                        <Typography sx={{fontSize: 12.5, color: Grey7}}>
                            {when(one.EventDatetime)}
                        </Typography>
                        <Typography sx={{fontSize: 12.5, color: Grey8}}>
                            {spell(one.EventType)}
                            {one.PagesRead ? ` — ${one.PagesRead} page(s)` : ""}
                        </Typography>
                        <Typography sx={{fontSize: 12.5, color: Grey7}}>
                            {one.Detail}
                        </Typography>
                    </Box>
                ))}
            </Box>
        )}
    </Box>;
}
